import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { GenericTimelyAccountId, GenericTimelyUserId, GenericUserID } from '../models.generic';

extendZodWithOpenApi(z);

export type AgentResponse = z.infer<typeof AgentResponseSchema>;
export const AgentResponseSchema = z
  .object({
    timely_account_id: GenericTimelyAccountId,
    timely_user_id: GenericTimelyUserId,
    session_id: z.string().optional(),
    response_type: z.enum(['intervals', 'text']),
    event: z.enum(['request_processed', 'request_failed']),
    range_started_at: z.string().datetime({ offset: true }),
    range_ended_at: z.string().datetime({ offset: true }),
    infered_dates: z.array(z.string()),
    response_text: z.string(),
    intervals: z.array(
      z
        .object({
          started_at: z.string().datetime({ offset: true }),
          ended_at: z.string().datetime({ offset: true }),
          predictions: z.array(
            z
              .object({
                timely_project_id: z.number(),
                timely_label_ids: z.array(z.number()),
                confidence: z.number(),
                note: z.string(),
                valid_as_hour: z.boolean(),
              })
              .passthrough()
          ),
        })
        .passthrough()
    ),
  })
  .passthrough();

export type GetAssistantResponseQueryParams = z.infer<typeof getAssistantResponseQueryParamsSchema>;
export const getAssistantResponseQueryParamsSchema = z
  .object({
    message: z.string().describe('The user message/prompt to send to the AI agent'),
    timely_account_id: GenericTimelyAccountId,
    session_id: z.string().optional().describe('Session ID to maintain conversation context'),
  })
  .passthrough();

export type GetAgentResponseQueryParams = z.infer<typeof getAgentResponseQueryParamsSchema>;
export const getAgentResponseQueryParamsSchema = getAssistantResponseQueryParamsSchema
  .extend({
    timely_user_id: GenericTimelyUserId,
    tic_user_id: GenericUserID,
  })
  .passthrough();

export type AgentPrediction = z.infer<typeof AgentPredictionSchema>;
export const AgentPredictionSchema = z.object({
  timely_project_id: z.number(),
  timely_label_ids: z.array(z.number()),
  confidence: z.number(),
});
export type TimelyPresentableAgentInterval = z.infer<typeof TimelyPresentableAgentIntervalSchema>;
export const TimelyPresentableAgentIntervalSchema = z
  .object({
    started_at: z.string().datetime({ offset: true }),
    ended_at: z.string().datetime({ offset: true }),
    total_seconds: z.number(),
    existing_hour_id: z.string().optional(),
    valid_as_hour: z.boolean(),
    note: z.string(),
    color: z.string(),
    labels: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        emoji: z.string().optional(),
      })
    ),
    prediction: AgentPredictionSchema,
    // just to make this compatible with isTicPredictionValidAsHour
    inferred: z.object({
      timely_project_id: z.number(),
      timely_label_ids: z.array(z.number()),
      confidence: z.number(),
      note: z.string(),
      valid_as_hour: z.boolean(),
    }),
  })
  .passthrough();

export type DatesToIntervalsMap = z.infer<typeof DatesToIntervalsMapSchema>;
export const DatesToIntervalsMapSchema = z.record(
  z.string(),
  z.array(TimelyPresentableAgentIntervalSchema)
);

export type TimelyPresentableAgentResponse = z.infer<typeof TimelyPresentableAgentResponseSchema>;
export const TimelyPresentableAgentResponseSchema = z
  .object({
    timely_account_id: GenericTimelyAccountId,
    timely_user_id: GenericTimelyUserId,
    infered_dates: z.array(z.string()),
    dates_to_intervals_map: DatesToIntervalsMapSchema,
    response_text: z.string(),
    total_seconds: z.number(),
    num_projects: z.number(),
    num_intervals: z.number(),
  })
  .passthrough();
